import { IconCheck, IconX } from "@tabler/icons-react";
import { useTranslation } from "react-i18next";
import BottomPanel from "../app/panels/BottomPanel";
import Skeleton from "../skeleton/Skeleton";
import { cn } from "../../lib/utils";
import { getArticleTypeText } from "../../domains/app/utils/getArticleTypeText";
import type { PracticeRailTerm } from "../../domains/practice/hooks/usePracticeRailTerms";
import type { PracticeFilterValuesData } from "../../domains/practice/hooks/usePracticeFilterValues";

/**
 * The drawer behind the rail's pinned chip: everything filtering the feed, in
 * the one place that shows it complete.
 *
 * Three axes, and they are not three of a kind. The MeSH term is the rail's own
 * chip repeated here — repetition is the point, since the pinned chip carries a
 * count and a "Clear all" that are unverifiable unless something lists what is
 * actually on. Journals and publication types exist ONLY here: they are
 * multi-select, so a bar of exclusive chips could not hold them.
 *
 * It NEVER touches the practice. That is the invariant the footer states out loud
 * rather than leaving to trust: the practice is the corpus, "Clear all" clears
 * filters, and a corpus is not a filter.
 *
 * ── What a count means ──────────────────────────────────────────────────────
 * The publications that value ALONE would open, inside the practice and the live
 * rail chip. Tick one box and the feed's total is the number you tapped. Tick a
 * second in the other axis and the numbers become upper bounds, because the two
 * axes intersect — the counts are computed once per drawer open and deliberately
 * not recomputed against each other (`filter-values.ts` has the reasoning and the
 * cost).
 *
 * ── An empty axis is not drawn ──────────────────────────────────────────────
 * A practice whose view holds no journal renders no "Journals" heading at all. An
 * empty heading reads as breakage, and there is nothing truthful to put under it.
 */
export type PracticeFeedFilters = {
  readonly journalUids: readonly string[];
  readonly publicationTypes: readonly string[];
};

function toggled(values: readonly string[], value: string): readonly string[] {
  return values.includes(value)
    ? values.filter((entry) => entry !== value)
    : [...values, value];
}

/**
 * One value of one axis.
 *
 * A square box and not a circle, because these axes are MULTI-SELECT and a circle
 * would promise one choice. `aria-pressed` rather than a real checkbox input:
 * the whole row is the target, which is what a thumb needs.
 */
function ValueRow({
  label,
  count,
  selected,
  onToggle,
}: {
  label: string;
  count: number;
  selected: boolean;
  onToggle: () => void;
}): React.JSX.Element {
  return (
    <button
      type="button"
      aria-pressed={selected}
      onClick={onToggle}
      className="flex w-full cursor-pointer items-center justify-between gap-3 py-2 text-left"
    >
      <span
        className={cn(
          "min-w-0 truncate text-ui-body",
          selected ? "font-semibold text-ds-primary" : "text-ds-black"
        )}
      >
        {label}
      </span>
      <span className="flex shrink-0 items-center gap-2">
        <span className="text-caption text-ds-slate-500">{count}</span>
        <span
          className={cn(
            "flex size-5 items-center justify-center rounded border",
            selected
              ? "border-ds-primary bg-ds-primary text-ds-on-primary"
              : "border-ds-slate-200 bg-white"
          )}
        >
          {selected ? <IconCheck size={14} stroke={2} /> : null}
        </span>
      </span>
    </button>
  );
}

function AxisHeading({ children }: { children: string }): React.JSX.Element {
  return (
    <h3 className="m-0 text-caption font-semibold tracking-wide text-ds-slate-500 uppercase">
      {children}
    </h3>
  );
}

/** Three rows of silhouette while the one request behind both axes lands. */
function AxisSkeleton(): React.JSX.Element {
  return (
    <div className="flex flex-col gap-3 py-2">
      {[0, 1, 2].map((row) => (
        <Skeleton key={row} height={20} />
      ))}
    </div>
  );
}

function MeshFilterDrawer({
  isOpen,
  onClose,
  activeFilterCount,
  practiceLabel,
  selectedTerm,
  onClearTerm,
  values,
  filters,
  onChangeFilters,
  onClearAll,
}: {
  isOpen: boolean;
  onClose: () => void;
  /** Counted once by the rail, so the badge and this header cannot disagree. */
  activeFilterCount: number;
  practiceLabel: string;
  /** The rail's live term. `null` when no chip is lit. */
  selectedTerm: PracticeRailTerm | null;
  /**
   * Clears the term and NOTHING else. It widens the view, and a wider view can
   * only hold more of each axis than the narrow one did — so every ticked box
   * survives it, and clearing one filter must not clear two.
   */
  onClearTerm: () => void;
  values: PracticeFilterValuesData;
  filters: PracticeFeedFilters;
  onChangeFilters: (filters: PracticeFeedFilters) => void;
  onClearAll: () => void;
}): React.JSX.Element {
  const { t } = useTranslation();
  // `unsupported` renders as neither axis, and is structurally unreachable: a
  // practice whose Boolean does not compile has no rail, hence no pinned chip and
  // no way in here. It is handled rather than thrown because the alternative is a
  // panel that crashes on a network blip.
  const journals = values.status === "ready" ? values.journals : [];
  const publicationTypes =
    values.status === "ready" ? values.publicationTypes : [];

  return (
    <BottomPanel
      isOpen={isOpen}
      onClose={onClose}
      displayCloseButton
      actions={
        <div className="flex flex-col gap-2">
          {/* No "Apply". The rail and these rows apply at the tap, and a drawer
              that needed validating would put two interaction models on one
              screen. */}
          <button
            type="button"
            disabled={activeFilterCount === 0}
            onClick={onClearAll}
            className={
              activeFilterCount === 0
                ? "self-start text-ui-body font-semibold text-ds-slate-400"
                : "cursor-pointer self-start text-ui-body font-semibold text-ds-primary"
            }
          >
            {t("Clear all")}
          </button>
          <p className="m-0 text-caption text-ds-slate-500">
            {t(
              "Your practice ({{practice}}) stays selected. Change it from the header.",
              { practice: practiceLabel }
            )}
          </p>
        </div>
      }
    >
      <div className="flex flex-col gap-6 px-5 pt-6 pb-2">
        <div className="flex items-baseline justify-between gap-2">
          <h2 className="m-0 text-heading-xs font-semibold text-ds-black">
            {t("Filters")}
          </h2>
          <span className="text-caption text-ds-slate-500">
            {t("{{total}} active", { total: activeFilterCount })}
          </span>
        </div>

        <section className="flex flex-col gap-2">
          <AxisHeading>{t("MeSH term")}</AxisHeading>
          {selectedTerm === null ? (
            <p className="m-0 text-footnote text-ds-slate-500">
              {t("None. Pick one from the rail.")}
            </p>
          ) : (
            <button
              type="button"
              onClick={onClearTerm}
              className="flex max-w-full cursor-pointer items-center gap-1 self-start rounded bg-ds-mint-100 px-2 py-1 text-caption font-semibold text-ds-mint-700"
            >
              {/* Already resolved for the reader's language server-side, like
                  every other chip — see `PracticeRailTerm`. */}
              <span className="min-w-0 truncate">{selectedTerm.label}</span>
              <IconX size={12} stroke={2} className="shrink-0" />
            </button>
          )}
        </section>

        {values.status === "loading" ? (
          <section className="flex flex-col gap-2">
            <AxisHeading>{t("Journals")}</AxisHeading>
            <AxisSkeleton />
          </section>
        ) : null}

        {journals.length > 0 && (
          <section className="flex flex-col gap-1">
            <AxisHeading>{t("Journals")}</AxisHeading>
            {journals.map((journal) => (
              <ValueRow
                key={journal.uid}
                // The venue's own name. Nothing translates a journal title.
                label={journal.name}
                count={journal.count}
                selected={filters.journalUids.includes(journal.uid)}
                onToggle={() => {
                  onChangeFilters({
                    journalUids: toggled(filters.journalUids, journal.uid),
                    publicationTypes: filters.publicationTypes,
                  });
                }}
              />
            ))}
          </section>
        )}

        {publicationTypes.length > 0 && (
          <section className="flex flex-col gap-1">
            <AxisHeading>{t("Publication types")}</AxisHeading>
            {publicationTypes.map((type) => (
              <ValueRow
                key={type.articleType}
                // Labelled the way every article card in the app labels it, from
                // the canonical string — so "RCT" here and "RCT" on the card,
                // in the reader's own locale.
                label={getArticleTypeText(type.articleType)}
                count={type.count}
                selected={filters.publicationTypes.includes(type.articleType)}
                onToggle={() => {
                  onChangeFilters({
                    journalUids: filters.journalUids,
                    publicationTypes: toggled(
                      filters.publicationTypes,
                      type.articleType
                    ),
                  });
                }}
              />
            ))}
          </section>
        )}
      </div>
    </BottomPanel>
  );
}

export { MeshFilterDrawer };
